import React from 'react';
import { API, apiList } from '../api';
import DataRow, { DataRowHeader } from './ui/DataRow';
import Toggle from './ui/Toggle';
import { sameNamePlayers, describePlayer } from '../utils/duplicateNames';

/*
 * Who is playing in this competition.
 *
 * This was a wall of tags: every player a chip carrying a checkbox, a paid
 * icon, a name, sometimes an amber "No Email" tag and a remove button, all
 * wrapping across the page. Chips are for a handful of short values; with
 * thirty players and four controls each, nothing lined up and there was no
 * way to scan down a single column — which is exactly what you want to do
 * here, to find who has not paid or who is missing an email address.
 *
 * It is now one row per player. The controls sit in fixed columns, so "who
 * has not paid" is a glance down one of them rather than a hunt.
 */
function CompetitionPlayers({ addAllPlayers, allEmailPlayersSelected, compPlayers, competitionPlayersWithEmail, existingPlayerId, isAdmin, isFinalised, isLocked, openProfile, playerEmailPanel, quickAddPlayer, quickName, quickRef, removePlayer, selectedComp, selectedCompPlayerIds, setActiveStep, setCompPlayers, setExistingPlayerId, setMatches, setQuickName, setSelectedCompPlayerIds, setTeams, showConfirm, showToast, teams, togglePaid, users }) {
  const unregistered = users.filter(u => !compPlayers.find(p => p.id === u.id));
  const paidCount = compPlayers.filter(p => p.hasPaid).length;
  const tracksPayment = compPlayers.some(p => p.hasPaid !== undefined);

  /* Somebody of this name is already at the club.
   *
     Two entries reading "Test" on a draw sheet is nobody's idea of a list,
     and almost every time it happens it is the same person typed twice
     rather than two people who share a name. So it asks, and the easy
     answer is the likely one — add the player who already exists.
   *
     It does not refuse, because clubs really do have two Bob Smiths. It
     just makes sure that when they do, somebody has decided it on purpose
     and has been nudged to tell them apart on the sheet. */
  const addWithNameCheck = async () => {
    const name = String(quickName || '').trim();
    if (!name) return;
    const same = sameNamePlayers(users, name);

    if (same.length) {
      const alreadyPlaying = same.find(u => compPlayers.some(p => p.id === u.id));
      if (alreadyPlaying) {
        showToast(`${alreadyPlaying.name} is already in this competition.`, 'red');
        return;
      }
      /* More than one of them already. An OK/Cancel box cannot ask which,
         and picking the first would be the app guessing at somebody's
         identity — so it does not offer. The club picker beside this box
         lists them, and that is where a choice between people belongs. */
      if (same.length > 1) {
        const addAnother = await showConfirm(
          `There are already ${same.length} players called "${name}" at this club:\n\n`
          + same.map(u => `  • ${describePlayer(u) || 'nothing recorded to tell them apart'}`).join('\n')
          + '\n\nTo enter one of them, cancel and use "Add from club…".\n\n'
          + 'OK — add another, different person of the same name.\n'
          + 'Cancel — go back.'
        );
        if (!addAnother) return;
        quickAddPlayer();
        return;
      }

      const existing = same[0];
      const detail = describePlayer(existing);
      /* The detail is the whole question. Asking "is this them?" while
         showing only the name — the one thing that already matches — invites
         a yes that means nothing, and the wrong Bob Smith ends up in the
         draw. Where there is nothing to go on, say so, so the answer is a
         judgement rather than a shrug. */
      const useExisting = await showConfirm(
        `There is already a player called "${existing.name}" at this club.\n\n`
        + (detail
          ? `That one is: ${detail}\n\nIs that the same person?\n\n`
          : 'There is nothing else recorded about them — no email, no '
            + 'membership number — so there is no way to be sure from here.\n\n'
            + 'If you are not certain, add a new person instead.\n\n')
        + 'OK — yes, add that player to this competition.\n'
        + 'Cancel — no, this is somebody different.'
      );
      if (useExisting) {
        setQuickName('');
        addExisting(String(existing.id));
        return;
      }
      const addAnyway = await showConfirm(
        `Add a second, different player also called "${name}"?\n\n`
        + 'Two identical names are hard to tell apart on a draw sheet or a '
        + 'score card. Consider something that separates them — a middle '
        + 'initial, or Jnr and Snr.\n\n'
        + 'OK — add them as they are.\n'
        + 'Cancel — go back and change the name.'
      );
      if (!addAnyway) return;
    }
    quickAddPlayer();
  };

  const addExisting = val => {
    setExistingPlayerId(val);
    if (!val) return;
    fetch(`${API}/api/competitions/${selectedComp.id}/players`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: parseInt(val) }),
    }).then(r => { if (!r.ok) throw new Error('Failed'); return r.json(); })
      .then(user => {
        if (user && user.name) setCompPlayers(prev => [...prev, user]);
        setExistingPlayerId('');
        setMatches([]);
      })
      .catch(() => showToast('Failed to add player', 'red'));
  };

  const toggleEmailPick = p => setSelectedCompPlayerIds(prev => {
    if (prev.includes(p.id)) return prev.filter(id => id !== p.id);
    if (!p.email) return prev;
    return [...prev, p.id];
  });

  /* Columns are conditional — a player looking at the page gets neither the
     email picker nor the paid switch — so they are assembled rather than
     written out, and the header takes the same array. */
  const columns = [
    isAdmin && {
      key: 'pick', width: 18,
      render: p => (
        <input
          type="checkbox"
          className="gs-check"
          checked={selectedCompPlayerIds.includes(p.id)}
          disabled={!p.email}
          aria-label={p.email ? `Email ${p.name}` : `${p.name} has no email address`}
          title={p.email ? 'Select for email' : 'No email address'}
          onChange={() => toggleEmailPick(p)} />
      ),
    },
    { key: 'name', label: 'Name', grow: true, primary: true },
    {
      key: 'email', label: 'Email', width: 190,
      render: p => (p.email
        ? <span style={{ color: 'var(--gs-text-muted)' }}>{p.email}</span>
        // Worth calling out rather than leaving blank: no email means this
        // player cannot be sent the draw.
        : <span style={{ color: '#b08900' }}>No email</span>),
    },
    isAdmin && tracksPayment && {
      key: 'paid', label: 'Paid', width: 70, align: 'center',
      render: p => (
        <Toggle
          ariaLabel={`Paid — ${p.name}`}
          checked={!!p.hasPaid}
          onChange={() => togglePaid(p.id)} />
      ),
    },
    !isLocked && {
      key: 'remove', width: 30, align: 'right',
      render: p => (
        <button
          className="w3-button w3-round w3-hover-red"
          style={{ padding: '4px 8px', color: 'var(--gs-text-faint)' }}
          title={`Remove ${p.name}`} aria-label={`Remove ${p.name}`}
          onClick={() => removePlayer(p.id)}>
          <i className="fa fa-times"></i>
        </button>
      ),
    },
  ].filter(Boolean);

  return (
    <div className="w3-card w3-round w3-white w3-margin-top">
      <div className="w3-container w3-padding">

        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--gs-space-3)', flexWrap: 'wrap' }}>
          <h4 style={{ margin: 0 }}>Players</h4>
          <span style={{ color: 'var(--gs-text-muted)', fontSize: 'var(--gs-text-sm)' }}>
            {compPlayers.length} registered{tracksPayment ? ` · ${paidCount} paid` : ''}
          </span>
          {isAdmin && competitionPlayersWithEmail.length > 0 && (
            <button
              className="w3-button w3-round w3-theme-l4"
              style={{ marginLeft: 'auto', minHeight: 'var(--gs-control-sm)' }}
              onClick={() => setSelectedCompPlayerIds(
                allEmailPlayersSelected ? [] : competitionPlayersWithEmail.map(p => p.id)
              )}>
              {allEmailPlayersSelected ? 'Clear email picks' : `Select all (${competitionPlayersWithEmail.length})`}
            </button>
          )}
        </div>

        {isLocked && (
          <div className="w3-panel w3-pale-yellow w3-leftbar w3-border-amber w3-round" style={{ padding: '8px 12px' }}>
            <i className="fa fa-lock w3-margin-right"></i>
            <strong className="w3-small">Competition {isFinalised ? 'finalised' : 'reopened for score fixes only'}</strong>
            <span className="w3-small"> — {isFinalised ? 'reopen from the Results tab to make changes' : 'player changes are locked'}</span>
          </div>
        )}

        {!isLocked && (
          <div style={{ display: 'flex', gap: 'var(--gs-space-3)', marginTop: 'var(--gs-space-4)', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 'var(--gs-space-2)', flex: '1 1 320px', minWidth: 0 }}>
              <input
                ref={quickRef} className="w3-input" style={{ flex: 1, minWidth: 0 }}
                placeholder="Type a name and press Enter"
                aria-label="New player name"
                value={quickName}
                onChange={e => setQuickName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addWithNameCheck()} />
              <button
                className="w3-button w3-round"
                style={{ background: 'var(--gs-accent-fill, var(--club-30))', color: '#fff', fontWeight: 600, minHeight: 'var(--gs-control-md)', padding: '0 22px' }}
                onClick={addWithNameCheck}>
                Add
              </button>
            </div>
            {unregistered.length > 0 && (
              <div style={{ display: 'flex', gap: 'var(--gs-space-2)', flex: '1 1 320px', minWidth: 0 }}>
                <select
                  className="w3-select" style={{ flex: 1, minWidth: 0 }}
                  aria-label="Add an existing club player"
                  value={existingPlayerId}
                  onChange={e => addExisting(e.target.value)}>
                  <option value="">Add from club…</option>
                  {unregistered.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
                <button
                  className="w3-button w3-round w3-theme-l4"
                  style={{ minHeight: 'var(--gs-control-md)', whiteSpace: 'nowrap' }}
                  onClick={addAllPlayers}
                  title="Add all unregistered club players">
                  Add all
                </button>
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: 'var(--gs-space-6)' }}>
          {compPlayers.length === 0 ? (
            <p style={{ color: 'var(--gs-text-muted)', textAlign: 'center', padding: 'var(--gs-space-6) 0' }}>
              No players registered yet.
            </p>
          ) : (
            <>
              <DataRowHeader columns={columns} />
              {compPlayers.map(p => (
                <DataRow key={p.id} columns={columns} item={p} onClick={() => openProfile(p.id)} />
              ))}
            </>
          )}
          {isAdmin && competitionPlayersWithEmail.length > 0 && (
            <p style={{ color: 'var(--gs-text-faint)', fontSize: 'var(--gs-text-sm)', marginTop: 'var(--gs-space-3)' }}>
              {selectedCompPlayerIds.length} of {competitionPlayersWithEmail.length} with an email address selected to receive email.
            </p>
          )}
        </div>

        {playerEmailPanel}

        <div style={{ textAlign: 'right', marginTop: 'var(--gs-space-6)', paddingBottom: '4px' }}>
          <button
            className="w3-button w3-round"
            style={{ background: 'var(--gs-action-fill, var(--club-10))', color: '#fff', fontWeight: 600, minHeight: 'var(--gs-control-lg)', padding: '0 26px' }}
            disabled={compPlayers.length === 0}
            onClick={async () => {
              if (selectedComp.format !== 'singles') {
                setActiveStep('teams');
                return;
              }
              /* Singles generates a team per player on the way through to the
                 draw. This used to write the response straight into `teams`
                 and move on regardless — so a refusal put { error: ... }
                 where the fixtures step expects an array, and the next render
                 called teams.map and took the whole app down to a blank page.
                 Nothing moves unless the teams actually came back. */
              try {
                setTeams(await apiList(`/api/competitions/${selectedComp.id}/teams/generate`, { method: 'POST' }));
                setActiveStep('fixtures');
              } catch (err) {
                showToast(err.message || 'Could not generate the draw', 'red');
              }
            }}>
            {selectedComp.format === 'singles' ? 'Next: generate draw' : 'Next: set up teams'}
            <i className="fa fa-arrow-right w3-margin-left"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

export default CompetitionPlayers;
